import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import { UmmFallback } from '../../umm-fallback';
import type { UmmsOverviewParams } from 'src/apis/iip-service/types';
import type { IipServiceRequest } from 'src/apis/iip-service/use-iip-service';
import { useUmm } from 'src/apis/iip-service/use-umm';
import { useUmmVersions } from 'src/apis/iip-service/use-umm-versions';
import { useUmms } from 'src/apis/iip-service/use-umms';
import { handleUmmRelatedError } from 'src/apis/iip-service/utils';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useUmmsPageParams } from 'src/pages/market-messages/urgent-market-messages/overview/use-page-params';

const Page = lazy(() => import('./page'));

type Props = {
  iipService: Successful<IipServiceRequest>;
};

export const UrgentMarketMessageDetails: FC<Props> = ({ iipService }) => {
  const params = useUmmsPageParams();

  const transformedParams = useMemo(() => {
    const { start, pageSize } = params.value;
    return {
      offset: start,
      limit: pageSize,
    } satisfies UmmsOverviewParams;
  }, [params.value]);

  const umms = useUmms({
    iipService,
    params: transformedParams,
    onError: handleUmmRelatedError,
  });
  const umm = useUmm({ ummsRequest: umms, onError: handleUmmRelatedError });
  const versions = useUmmVersions({
    ummRequest: umm,
    onError: handleUmmRelatedError,
  });

  if (umms.error || umm.error || versions.error) return <UmmFallback />;
  if (
    !umm.response ||
    (umm.response.data._links?.versions && !versions.response)
  ) {
    return <PageSpinner />;
  }

  return <Page umm={umm} versions={versions} />;
};
